/**
 * כפתור — הרכיב היחיד שדרכו נוצרים כפתורי האתר.
 *
 * 🔴 **החלטת עיצוב, טל 10.08.2026:** ה-CTA הוא **מסגרת נחושת · רקע לבן · טקסט כהה**.
 *    ⛔ הכפתור המלא בנחושת נפסל — טקסט לבן עליו נותן 3.79:1, מתחת ל-WCAG AA.
 *    המספרים והנימוק ב-`index.css` תחת `.btn-cta`.
 *
 * ⚠️ ⛔ אין ליצור כפתור עם `<div onClick>` או עם מחלקות Tailwind ישירות.
 *    כל כפתור עובר כאן — אחרת הניגודיות, יעד המגע והפוקוס נשחקים כל אחד בנפרד.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'cta' | 'secondary';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  children: ReactNode;
}

const VARIANT_CLASS: Record<Variant, string> = {
  cta: 'btn btn-cta',
  secondary: 'btn btn-secondary',
};

export function Button({ variant = 'cta', className, children, ...rest }: ButtonProps) {
  const classes = [VARIANT_CLASS[variant], className].filter(Boolean).join(' ');

  return (
    <button type="button" className={classes} {...rest}>
      {children}
    </button>
  );
}

/**
 * כפתור שהוא בפועל קישור.
 *
 * ⚠️ נדרש כי הזרימה של נועם מובילה ל-`wa.me` ול-`mailto:` — יעדים שהם **ניווט**,
 *    ⛔ לא פעולה. שימוש ב-`<button onClick={location.href=…}>` שובר פתיחה בלשונית
 *    חדשה, גרירה, ו"העתק כתובת" — וקורא מסך מכריז "לחצן" במקום "קישור".
 */
interface LinkButtonProps {
  href: string;
  variant?: Variant;
  children: ReactNode;
  className?: string;
  /** ⚠️ ל-`target="_blank"` נוסף אוטומטית `rel` — בלעדיו זו פרצת tabnabbing */
  newTab?: boolean;
}

export function LinkButton({
  href,
  variant = 'cta',
  children,
  className,
  newTab = false,
}: LinkButtonProps) {
  const classes = [VARIANT_CLASS[variant], className].filter(Boolean).join(' ');

  return (
    <a
      href={href}
      className={classes}
      {...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {children}
    </a>
  );
}
